import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import { useAuditLog } from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Select } from '../../../shell/Field.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDateTime } from '../../../lib/format.js';
import type { AuditEntry } from '@shared/types';

const actionTone: Record<string, 'green' | 'yellow' | 'red' | 'blue' | 'slate'> = {
  create: 'green', update: 'yellow', delete: 'red',
  share: 'blue', unshare: 'slate', approve: 'green', deny: 'red',
  acknowledge: 'blue', sign_in: 'slate', sign_out: 'slate',
};

export function AuditPage() {
  const [entityType, setEntityType] = useState<string | undefined>(undefined);
  const { data, isLoading } = useAuditLog({ entityType });

  const columns: Column<AuditEntry>[] = [
    { key: 'at', header: 'Timestamp', render: (a) => formatDateTime(a.at) },
    { key: 'actor', header: 'Actor', render: (a) => <span className="font-mono text-xs">{a.actorUserId.slice(-8)}</span> },
    { key: 'action', header: 'Action', render: (a) => <Badge tone={actionTone[a.action] ?? 'slate'}>{a.action}</Badge> },
    { key: 'entity', header: 'Entity Type', render: (a) => a.entityType },
    { key: 'entityId', header: 'Entity ID', render: (a) => a.entityId ? <span className="font-mono text-xs">{a.entityId.slice(-8)}</span> : '—' },
    { key: 'ip', header: 'IP', render: (a) => <span className="text-xs text-slate-400 dark:text-slate-500">{a.ip ?? '—'}</span> },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Audit log</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Append-only record of all state-changing actions</p>
      </div>

      <div className="max-w-xs">
        <Select value={entityType ?? ''} onChange={(e) => setEntityType(e.target.value || undefined)}>
          <option value="">All entity types</option>
          <option value="employee">Employees</option>
          <option value="review">Reviews</option>
          <option value="recognition">Recognition</option>
          <option value="disciplinary_action">Disciplinary</option>
          <option value="documented_conversation">Conversations</option>
          <option value="employee_file_note">File Notes</option>
          <option value="time_off_request">Time off</option>
          <option value="document">Documents</option>
          <option value="user">Users</option>
        </Select>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState icon={<ScrollText className="h-8 w-8" />} title="No audit entries" description="Actions will be logged here." />
      )}
    </div>
  );
}
